export const COPILOT_IS_OPEN_QUERY_PARAM = 'copilotIsOpen'
export const COPILOT_CHAT_ID_QUERY_PARAM = 'copilotChatId'

const CHAT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface CopilotUrlState {
  isOpen: boolean
  chatId: string | null
}

export interface ParsedCopilotUrlState extends CopilotUrlState {
  /** `copilotChatId` was present but not a UUID, so `chatId` is null although the link asked for a chat. */
  chatIdInvalid: boolean
}

/**
 * Reads drawer state from a query string. A chat id that is not a UUID is dropped,
 * so a hand-edited or garbage value never reaches the chat API. A chat id param, valid
 * or not, also opens the drawer: a link carrying only `copilotChatId` still shows that
 * chat, and a broken one shows the "chat not found" notice instead of doing nothing.
 */
export const parseCopilotUrlState = (search: string): ParsedCopilotUrlState => {
  const params = new URLSearchParams(search)
  const rawChatId = params.get(COPILOT_CHAT_ID_QUERY_PARAM)
  const chatId = rawChatId != null && CHAT_ID_PATTERN.test(rawChatId) ? rawChatId : null

  return {
    isOpen: params.get(COPILOT_IS_OPEN_QUERY_PARAM) === 'true' || rawChatId != null,
    chatId,
    chatIdInvalid: rawChatId != null && chatId == null
  }
}

/** Returns the query string with copilot params applied; every other param is untouched. */
export const buildCopilotSearch = (search: string, { isOpen, chatId }: CopilotUrlState): string => {
  const params = new URLSearchParams(search)

  if (isOpen) {
    params.set(COPILOT_IS_OPEN_QUERY_PARAM, 'true')
    if (chatId == null) {
      params.delete(COPILOT_CHAT_ID_QUERY_PARAM)
    } else {
      params.set(COPILOT_CHAT_ID_QUERY_PARAM, chatId)
    }
  } else {
    params.delete(COPILOT_IS_OPEN_QUERY_PARAM)
    params.delete(COPILOT_CHAT_ID_QUERY_PARAM)
  }

  const next = params.toString()
  return next === '' ? '' : `?${next}`
}

/**
 * Mirrors drawer state into the address bar with `history.replaceState`: it reads the
 * live `location` each call (no stale router snapshot), adds no history entry and
 * triggers no server round trip. No-ops when nothing would change.
 */
export const writeCopilotUrlState = (state: CopilotUrlState): void => {
  if (typeof window === 'undefined') return

  const { pathname, search, hash } = window.location
  const nextSearch = buildCopilotSearch(search, state)
  if (nextSearch === search) return

  window.history.replaceState(window.history.state, '', `${pathname}${nextSearch}${hash}`)
}
